import { useState, useEffect } from "react";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { 
  Plus, Search, Briefcase, Users, MapPin, DollarSign, ArrowRight, Sparkles, CheckCircle2, XCircle, ClipboardList
} from "lucide-react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const stages = [
  { id: "applied", label: "Aplicado", color: "bg-slate-100 text-slate-600" },
  { id: "screening", label: "Filtrado", color: "bg-blue-100 text-blue-600" },
  { id: "interview", label: "Entrevista", color: "bg-violet-100 text-violet-600" },
  { id: "offer", label: "Oferta", color: "bg-amber-100 text-amber-600" },
  { id: "hired", label: "Contratado", color: "bg-emerald-100 text-emerald-600" },
  { id: "rejected", label: "Rechazado", color: "bg-red-100 text-red-600" }
];

const Recruitment = () => {
  const [vacancies, setVacancies] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [users, setUsers] = useState([]);
  const [reviewTasks, setReviewTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vacancyDialogOpen, setVacancyDialogOpen] = useState(false);
  const [compareDialogOpen, setCompareDialogOpen] = useState(false);
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [selectedVacancy, setSelectedVacancy] = useState(null);
  const [compareFiles, setCompareFiles] = useState([]);
  const [compareResults, setCompareResults] = useState([]);
  const [selectedMatchIds, setSelectedMatchIds] = useState([]);
  const [selectedAssigneeIds, setSelectedAssigneeIds] = useState([]);
  
  const [vacancyForm, setVacancyForm] = useState({
    title: "", department: "", description: "", requirements: "", salary_range: "", location: "", requester_user_id: "", requester_name: "", headcount: 1, assigned_manager_ids: [], assigned_manager_names: []
  });
  const [requestForm, setRequestForm] = useState({ title: "", description: "", headcount: 1, priority: "medium" });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const [vacanciesRes, candidatesRes, employeesRes, usersRes, reviewRes] = await Promise.all([
        axios.get(`${API}/vacancies`, { headers, withCredentials: true }),
        axios.get(`${API}/candidates`, { headers, withCredentials: true }),
        axios.get(`${API}/employees`, { headers, withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/users`, { headers, withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/recruitment/review-tasks`, { headers, withCredentials: true }).catch(() => ({ data: [] }))
      ]);
      setVacancies(vacanciesRes.data);
      setCandidates(candidatesRes.data);
      setEmployees(employeesRes.data || []);
      setUsers(usersRes.data || []);
      setReviewTasks(reviewRes.data || []);
    } catch (error) {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateVacancy = async (e) => {
    e.preventDefault();
    if (!vacancyForm.title || !vacancyForm.department) {
      toast.error("Completa los campos requeridos");
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/vacancies`, {
        ...vacancyForm,
        requirements: vacancyForm.requirements.split('\n').filter(r => r.trim())
      }, { headers, withCredentials: true });
      toast.success("Vacante creada");
      setVacancyDialogOpen(false);
      setVacancyForm({ title: "", department: "", description: "", requirements: "", salary_range: "", location: "", requester_user_id: "", requester_name: "", headcount: 1, assigned_manager_ids: [], assigned_manager_names: [] });
      fetchData();
    } catch (error) {
      toast.error("Error al crear vacante");
    }
  };

  const handleUpdateStage = async (candidateId, newStage) => {
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.put(`${API}/candidates/${candidateId}/stage?stage=${newStage}`, {}, { headers, withCredentials: true });
      toast.success("Etapa actualizada");
      fetchData();
    } catch (error) {
      toast.error("Error al actualizar etapa");
    }
  };

  const getCandidatesForVacancy = (vacancyId) => candidates.filter(c => c.vacancy_id === vacancyId);
  const getStageLabel = (stageId) => stages.find(s => s.id === stageId)?.label || stageId;
  const getStageColor = (stageId) => stages.find(s => s.id === stageId)?.color || "bg-slate-100";
  const managerUsers = users.filter(account => ["supervisor", "gerente_rh", "admin_empresa", "super_admin"].includes(account.role));

  const toggleArrayValue = (value, current, setter) => {
    setter(current.includes(value) ? current.filter(item => item !== value) : [...current, value]);
  };

  const handleCompareCVs = async () => {
    if (!selectedVacancy || compareFiles.length === 0) { toast.error("Selecciona una vacante y al menos un CV"); return; }
    try {
      const token = localStorage.getItem("token");
      const fd = new FormData();
      Array.from(compareFiles).forEach(file => fd.append("files", file));
      const response = await axios.post(`${API}/vacancies/${selectedVacancy.vacancy_id}/compare-cvs`, fd, { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }, withCredentials: true });
      setCompareResults(response.data.matches || []);
      setSelectedMatchIds((response.data.matches || []).slice(0, 3).map(item => item.match_id));
      toast.success("CVs comparados y asignados al responsable de la vacante");
    } catch (error) {
      toast.error(error.response?.data?.detail || "No se pudieron comparar los CVs");
    }
  };

  const handleAssignMatches = async () => {
    if (!selectedVacancy || selectedMatchIds.length === 0 || selectedAssigneeIds.length === 0) { toast.error("Selecciona candidatos y jefaturas"); return; }
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/vacancies/${selectedVacancy.vacancy_id}/assign-matches`, { match_ids: selectedMatchIds, assignee_user_ids: selectedAssigneeIds }, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true });
      toast.success("Shortlist asignada al responsable");
      setCompareDialogOpen(false);
      setSelectedAssigneeIds([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "No se pudo asignar la shortlist");
    }
  };

  const handleRequestPosition = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/vacancy-requests`, requestForm, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true });
      toast.success("Solicitud de posición creada como ticket");
      setRequestDialogOpen(false);
      setRequestForm({ title: "", description: "", headcount: 1, priority: "medium" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "No se pudo crear la solicitud");
    }
  };

  const handleReviewDecision = async (taskId, status) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/recruitment/review-tasks/${taskId}`, { status }, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true });
      toast.success(status === "interview_requested" ? "Candidato marcado para cita" : "Candidato descartado");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "No se pudo actualizar la tarea");
    }
  };

  return (
    <Layout 
      title="Reclutamiento" 
      subtitle="Gestión de vacantes y candidatos"
      actions={
        <div className="flex gap-2">
          <Dialog open={requestDialogOpen} onOpenChange={setRequestDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="request-position-btn"><ClipboardList className="w-4 h-4 mr-2" />Solicitar posición</Button>
            </DialogTrigger>
            <DialogContent className="max-w-xl">
              <DialogHeader><DialogTitle>Solicitud de posición vía ticket</DialogTitle></DialogHeader>
              <form onSubmit={handleRequestPosition} className="space-y-4">
                <div className="space-y-2"><Label>Título</Label><Input value={requestForm.title} onChange={e => setRequestForm(p => ({ ...p, title: e.target.value }))} data-testid="request-position-title-input" /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Vacantes requeridas</Label><Input type="number" min="1" value={requestForm.headcount} onChange={e => setRequestForm(p => ({ ...p, headcount: Number(e.target.value || 1) }))} data-testid="request-position-headcount-input" /></div>
                  <div className="space-y-2"><Label>Prioridad</Label><Select value={requestForm.priority} onValueChange={value => setRequestForm(p => ({ ...p, priority: value }))}><SelectTrigger data-testid="request-position-priority-select"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="low">Baja</SelectItem><SelectItem value="medium">Media</SelectItem><SelectItem value="high">Alta</SelectItem></SelectContent></Select></div>
                </div>
                <div className="space-y-2"><Label>Descripción</Label><Textarea value={requestForm.description} onChange={e => setRequestForm(p => ({ ...p, description: e.target.value }))} rows={3} data-testid="request-position-description-input" /></div>
                <DialogFooter><Button type="submit" className="bg-violet-600 hover:bg-violet-700" data-testid="submit-request-position-btn">Crear ticket</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          <Link to="/ai-agent/recruitment">
            <Button variant="outline" className="border-violet-200 text-violet-600 hover:bg-violet-50">
              <Sparkles className="w-4 h-4 mr-2" />
              Asistente IA
            </Button>
          </Link>
          <Dialog open={vacancyDialogOpen} onOpenChange={setVacancyDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-violet-600 hover:bg-violet-700" data-testid="create-vacancy-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nueva Vacante
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Crear Vacante</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateVacancy} className="space-y-4">
                <div className="space-y-2">
                  <Label>Título del Puesto *</Label>
                  <Input
                    value={vacancyForm.title}
                    onChange={(e) => setVacancyForm(p => ({ ...p, title: e.target.value }))}
                    placeholder="Desarrollador Full Stack"
                    data-testid="vacancy-title-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Departamento *</Label>
                    <Input
                      value={vacancyForm.department}
                      onChange={(e) => setVacancyForm(p => ({ ...p, department: e.target.value }))}
                      placeholder="Tecnología"
                      data-testid="vacancy-department-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Ubicación</Label>
                    <Input
                      value={vacancyForm.location}
                      onChange={(e) => setVacancyForm(p => ({ ...p, location: e.target.value }))}
                      placeholder="CDMX / Remoto"
                      data-testid="vacancy-location-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Responsable de la vacante</Label>
                  <Select value={vacancyForm.requester_user_id} onValueChange={(value) => {
                    const requester = employees.find(employee => employee.employee_id === value);
                    setVacancyForm(p => ({ ...p, requester_user_id: value, requester_name: requester ? `${requester.first_name} ${requester.last_name}` : "" }));
                  }}>
                    <SelectTrigger data-testid="vacancy-requester-select"><SelectValue placeholder="Selecciona jefe solicitante" /></SelectTrigger>
                    <SelectContent>{employees.map(employee => <SelectItem key={employee.employee_id} value={employee.employee_id}>{employee.first_name} {employee.last_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Número de posiciones</Label><Input type="number" min="1" value={vacancyForm.headcount} onChange={e => setVacancyForm(p => ({ ...p, headcount: Number(e.target.value || 1) }))} data-testid="vacancy-headcount-input" /></div>
                  <div className="space-y-2"><Label>Jefaturas asignadas</Label><div className="max-h-28 overflow-y-auto border rounded-lg p-2 space-y-1" data-testid="vacancy-assigned-managers-list">{managerUsers.map(manager => <label key={manager.user_id} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={vacancyForm.assigned_manager_ids.includes(manager.user_id)} onChange={() => {
                    const nextIds = vacancyForm.assigned_manager_ids.includes(manager.user_id) ? vacancyForm.assigned_manager_ids.filter(item => item !== manager.user_id) : [...vacancyForm.assigned_manager_ids, manager.user_id];
                    const nextNames = managerUsers.filter(item => nextIds.includes(item.user_id)).map(item => item.name);
                    setVacancyForm(p => ({ ...p, assigned_manager_ids: nextIds, assigned_manager_names: nextNames }));
                  }} data-testid={`vacancy-manager-checkbox-${manager.user_id}`} />{manager.name}</label>)}</div></div>
                </div>
                <div className="space-y-2">
                  <Label>Rango Salarial</Label>
                  <Input
                    value={vacancyForm.salary_range}
                    onChange={(e) => setVacancyForm(p => ({ ...p, salary_range: e.target.value }))}
                    placeholder="$30,000 - $45,000 MXN"
                    data-testid="vacancy-salary-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Descripción</Label>
                  <Textarea
                    value={vacancyForm.description}
                    onChange={(e) => setVacancyForm(p => ({ ...p, description: e.target.value }))}
                    placeholder="Descripción del puesto..."
                    rows={3}
                    data-testid="vacancy-description-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Requisitos (uno por línea)</Label>
                  <Textarea
                    value={vacancyForm.requirements}
                    onChange={(e) => setVacancyForm(p => ({ ...p, requirements: e.target.value }))}
                    placeholder="3+ años de experiencia&#10;Conocimiento en React&#10;Inglés intermedio"
                    rows={3}
                    data-testid="vacancy-requirements-input"
                  />
                </div>
                <DialogFooter>
                  <Button type="submit" className="bg-violet-600 hover:bg-violet-700" data-testid="submit-vacancy-btn">
                    Crear Vacante
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      }
    >
      {/* Stats */}
      <div className="grid md:grid-cols-4 gap-4 mb-6">
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-violet-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{vacancies.filter(v => v.status === "open").length}</p>
                <p className="text-sm text-slate-500">Vacantes Abiertas</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{candidates.length}</p>
                <p className="text-sm text-slate-500">Total Candidatos</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{candidates.filter(c => c.stage === "interview").length}</p>
                <p className="text-sm text-slate-500">En Entrevista</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{candidates.filter(c => c.stage === "hired").length}</p>
                <p className="text-sm text-slate-500">Contratados</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="vacancies" className="space-y-6">
        <TabsList>
          <TabsTrigger value="vacancies" data-testid="tab-vacancies">Vacantes</TabsTrigger>
          <TabsTrigger value="pipeline" data-testid="tab-pipeline">Pipeline</TabsTrigger>
          <TabsTrigger value="reviews" data-testid="tab-reviews">Revisiones</TabsTrigger>
        </TabsList>

        <TabsContent value="vacancies">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-4 border-violet-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : vacancies.length === 0 ? (
            <Card className="border-slate-200">
              <CardContent className="flex flex-col items-center justify-center h-64 text-slate-500">
                <Briefcase className="w-12 h-12 mb-3 text-slate-300" />
                <p className="mb-4">No hay vacantes creadas</p>
                <Button onClick={() => setVacancyDialogOpen(true)} className="bg-violet-600 hover:bg-violet-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Crear Primera Vacante
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {vacancies.map((vacancy) => (
                <Card key={vacancy.vacancy_id} className="border-slate-200 card-hover">
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <Badge className={vacancy.status === "open" ? "badge-success" : "badge-error"}>
                        {vacancy.status === "open" ? "Abierta" : "Cerrada"}
                      </Badge>
                      <span className="text-xs text-slate-500">
                        {getCandidatesForVacancy(vacancy.vacancy_id).length} candidatos
                      </span>
                    </div>
                    <h3 className="font-semibold text-slate-900 mb-2">{vacancy.title}</h3>
                    <div className="space-y-2 text-sm text-slate-600 mb-4">
                      <div className="flex items-center gap-2">
                        <Briefcase className="w-4 h-4" />
                        {vacancy.department}
                      </div>
                      {vacancy.location && (
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4" />
                          {vacancy.location}
                        </div>
                      )}
                      {vacancy.salary_range && (
                        <div className="flex items-center gap-2">
                          <DollarSign className="w-4 h-4" />
                          {vacancy.salary_range}
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 mb-2">Posiciones: {vacancy.headcount || 1} · Jefaturas asignadas: {(vacancy.assigned_manager_names || []).join(", ") || vacancy.requester_name || "Pendiente"}</p>
                    <Button variant="outline" className="w-full mt-2" onClick={() => { setSelectedVacancy(vacancy); setCompareDialogOpen(true); setCompareResults([]); setSelectedAssigneeIds(vacancy.assigned_manager_ids || []); }} data-testid={`compare-cv-${vacancy.vacancy_id}`}>
                      <Sparkles className="w-4 h-4 mr-2" />Comparar múltiples CVs
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="pipeline">
          <div className="overflow-x-auto">
            <div className="flex gap-4 min-w-max pb-4">
              {stages.slice(0, 5).map((stage) => (
                <div key={stage.id} className="w-72">
                  <div className={`rounded-t-lg p-3 ${stage.color}`}>
                    <h3 className="font-semibold">{stage.label}</h3>
                    <p className="text-sm opacity-75">
                      {candidates.filter(c => c.stage === stage.id).length} candidatos
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-b-lg p-3 min-h-[300px] space-y-3">
                    {candidates.filter(c => c.stage === stage.id).map((candidate) => (
                      <Card key={candidate.candidate_id} className="border-slate-200">
                        <CardContent className="p-3">
                          <p className="font-medium text-slate-900">{candidate.name}</p>
                          <p className="text-sm text-slate-500">{candidate.email}</p>
                          <p className="text-xs text-slate-400 mt-1">
                            {vacancies.find(v => v.vacancy_id === candidate.vacancy_id)?.title}
                          </p>
                          <div className="flex gap-1 mt-3">
                            {stages.map((s, idx) => (
                              <Button
                                key={s.id}
                                size="sm"
                                variant={candidate.stage === s.id ? "default" : "ghost"}
                                className={`h-6 w-6 p-0 text-xs ${candidate.stage === s.id ? 'bg-violet-600' : ''}`}
                                onClick={() => handleUpdateStage(candidate.candidate_id, s.id)}
                              >
                                {idx + 1}
                              </Button>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="reviews">
          {reviewTasks.length === 0 ? <Card className="border-slate-200"><CardContent className="p-8 text-center text-slate-500" data-testid="review-tasks-empty">No hay tareas de revisión asignadas</CardContent></Card> : <div className="space-y-4" data-testid="review-tasks-list">{reviewTasks.map(task => (
            <Card key={task.task_id} className="border-slate-200"><CardContent className="p-5 space-y-3">
              <div className="flex items-center justify-between gap-3"><div><p className="font-semibold text-slate-900">{task.candidate_file_name}</p><p className="text-xs text-slate-500">Asignado a {task.assigned_to_name}</p></div><Badge className={task.status === 'interview_requested' ? 'bg-emerald-100 text-emerald-700' : task.status === 'discarded' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}>{task.status}</Badge></div>
              <p className="text-sm text-slate-700"><strong>Score:</strong> {task.candidate_summary?.compatibility_score || 0}%</p>
              {task.candidate_summary?.recommendation && <p className="text-sm text-slate-600">{task.candidate_summary.recommendation}</p>}
              <div className="flex gap-2"><Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => handleReviewDecision(task.task_id, 'interview_requested')} data-testid={`review-task-interview-${task.task_id}`}><CheckCircle2 className="w-4 h-4 mr-1" />Citar</Button><Button size="sm" variant="outline" onClick={() => handleReviewDecision(task.task_id, 'discarded')} data-testid={`review-task-discard-${task.task_id}`}><XCircle className="w-4 h-4 mr-1" />Descartar</Button></div>
            </CardContent></Card>
          ))}</div>}
        </TabsContent>
      </Tabs>

      <Dialog open={compareDialogOpen} onOpenChange={setCompareDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Comparar CVs con vacante</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">Vacante: <strong>{selectedVacancy?.title || "-"}</strong></p>
            <div className="space-y-2">
              <Label>Sube múltiples CVs</Label>
              <Input type="file" multiple onChange={(event) => setCompareFiles(event.target.files || [])} data-testid="compare-cvs-file-input" />
            </div>
            <Button onClick={handleCompareCVs} className="bg-violet-600 hover:bg-violet-700" data-testid="run-cv-compare-btn">Analizar compatibilidad</Button>
            {compareResults.length > 0 && <div className="space-y-3" data-testid="cv-compare-results">
              {compareResults.map(result => (
                <Card key={result.match_id} className="border-slate-200"><CardContent className="p-4 space-y-2">
                  <div className="flex items-center justify-between gap-3">
                    <label className="flex items-center gap-2 text-sm font-semibold text-slate-900"><input type="checkbox" checked={selectedMatchIds.includes(result.match_id)} onChange={() => toggleArrayValue(result.match_id, selectedMatchIds, setSelectedMatchIds)} data-testid={`compare-match-checkbox-${result.match_id}`} />{result.file_name}</label>
                    <Badge className="bg-emerald-100 text-emerald-700">{result.compatibility_score}%</Badge>
                  </div>
                  <p className="text-xs text-slate-500">Asignado a: {result.assigned_manager_name || result.assigned_manager_user_id || "Responsable no definido"}</p>
                  {result.strengths?.length > 0 && <div><p className="text-[11px] uppercase text-emerald-600 font-semibold">Fortalezas</p>{result.strengths.map((strength, index) => <p key={index} className="text-sm text-slate-700">• {strength}</p>)}</div>}
                  {result.risks?.length > 0 && <div><p className="text-[11px] uppercase text-amber-600 font-semibold">Riesgos</p>{result.risks.map((risk, index) => <p key={index} className="text-sm text-slate-700">• {risk}</p>)}</div>}
                  {result.recommendation && <p className="text-sm text-slate-700"><strong>Recomendación:</strong> {result.recommendation}</p>}
                </CardContent></Card>
              ))}
              <div className="p-4 rounded-xl border border-slate-200 bg-slate-50 space-y-3">
                <p className="text-sm font-semibold text-slate-900">Asignar shortlist a jefaturas</p>
                <div className="max-h-40 overflow-y-auto grid md:grid-cols-2 gap-2" data-testid="compare-assignees-list">{managerUsers.map(manager => <label key={manager.user_id} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={selectedAssigneeIds.includes(manager.user_id)} onChange={() => toggleArrayValue(manager.user_id, selectedAssigneeIds, setSelectedAssigneeIds)} data-testid={`compare-assignee-checkbox-${manager.user_id}`} />{manager.name}</label>)}</div>
                <Button onClick={handleAssignMatches} className="bg-emerald-600 hover:bg-emerald-700" data-testid="assign-shortlist-btn">Asignar seleccionados</Button>
              </div>
            </div>}
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Recruitment;
